import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Dialog,
  Flex,
  Input,
  Label,
  SwatchGroup,
  Text,
  useControllableState,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useTagsQuery } from '~publish/hooks/useTags'
import type { Tag } from '~publish/hooks/useTags'

import { colors, selectColor } from './colors'

import styles from './TagEditDialog.module.css'

export type TagFormValue = Pick<Tag, 'name' | 'color'> &
  Partial<Pick<Tag, 'id'>>

export interface TagEditDialogProps {
  children?: React.ReactNode
  tag?: Tag | { name: string; color?: string }
  onSubmit: (tag: TagFormValue) => void | Promise<void>
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface FormState {
  name: string
  color: string
  errorMessage: string | null
  saving: boolean
}

export function TagEditDialog(props: TagEditDialogProps): JSX.Element {
  const { tags } = useTagsQuery()
  return <TagEditDialogForm {...props} existingTags={tags} />
}

export function TagEditDialogForm({
  tag,
  onSubmit,
  onOpenChange: onOpenChangeProp,
  open: openProp,
  children,
  existingTags: tags,
}: TagEditDialogProps & {
  existingTags: readonly Pick<Tag, 'name' | 'color'>[]
}): JSX.Element {
  // State
  const [open, onOpenChange] = useControllableState({
    prop: openProp,
    onChange: onOpenChangeProp,
  })
  const editMode = tag != null && 'id' in tag && tag.id !== undefined

  const [formState, setFormState] = useState<FormState>({
    name: tag?.name ?? '',
    color: tag?.color ?? selectColor(tags.map((t) => t.color)),
    errorMessage: null,
    saving: false,
  })

  // Effects
  useEffect(() => {
    if (!open) {
      setFormState((prev) => ({
        ...prev,
        name: editMode && tag?.name ? tag.name : '',
        errorMessage: null,
        color: tag?.color ?? selectColor(tags.map((t) => t.color)),
      }))
    } else if (open && tag) {
      setFormState((prev) => ({
        ...prev,
        name: tag.name ?? '',
        color: tag.color ?? prev.color,
      }))
    }
  }, [open, tags, tag, editMode])

  useEffect(() => {
    const error = validateTagName(formState.name, tags, editMode)
    setFormState((prev) => ({ ...prev, errorMessage: error }))
  }, [formState.name, tags, editMode])

  // Handlers

  const handleSubmit = async (
    evt: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    evt.preventDefault()

    const error = validateTagName(formState.name, tags, editMode, {
      submitting: true,
    })
    setFormState((prev) => ({ ...prev, errorMessage: error }))
    if (formState.errorMessage || error) return

    const isUnchanged =
      tag?.name === formState.name && tag?.color === formState.color && editMode

    if (isUnchanged) {
      onOpenChange(false)
      return
    }

    setFormState((prev) => ({ ...prev, saving: true }))

    try {
      onOpenChange(false)
      await onSubmit({
        ...tag,
        name: formState.name,
        color: formState.color,
      })
    } finally {
      setFormState((prev) => ({ ...prev, saving: false }))
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      {children && <Dialog.Trigger>{children}</Dialog.Trigger>}
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>{editMode ? 'Edit Tag' : 'New Tag'}</Dialog.Title>
        </Dialog.Header>
        <VisuallyHidden>
          <Dialog.Description>
            {editMode
              ? 'This dialog allows you to edit an existing tag. You can change the tag name and color.'
              : 'This dialog allows you to create a new tag. You can set a name and color for your tag.'}
          </Dialog.Description>
        </VisuallyHidden>
        <Dialog.Body>
          <form onSubmit={handleSubmit}>
            {/* Name Input */}
            <Label htmlFor="tagName" className={styles.label}>
              Name
            </Label>
            <Input
              id="tagName"
              value={formState.name}
              aria-required={true}
              onChange={(evt): void =>
                setFormState((prev) => ({ ...prev, name: evt.target.value }))
              }
              aria-invalid={!!formState.errorMessage}
              aria-describedby={
                formState.errorMessage ? 'tagNameError' : undefined
              }
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {formState.errorMessage && (
              <Text
                id="tagNameError"
                color="critical"
                role="alert"
                className={styles.errorLabel}
              >
                {formState.errorMessage}
              </Text>
            )}

            {/* Color Picker */}
            <Label htmlFor="colorPicker" className={styles.label}>
              Color
            </Label>
            <VisuallyHidden>
              <div id="colorPickerDescription">
                Choose a color for your tag from the available color options.
                Use arrow keys to navigate between colors.
              </div>
            </VisuallyHidden>
            <SwatchGroup
              id="colorPicker"
              onChange={(evt): void =>
                setFormState((prev) => ({ ...prev, color: evt.target.value }))
              }
              className={styles.swatch}
              value={formState.color}
              aria-describedby="colorPickerDescription"
            >
              {colors.map(({ color, name }) => (
                <SwatchGroup.Swatch
                  key={color}
                  color={color}
                  name={name}
                  aria-label={`color ${color}`}
                />
              ))}
            </SwatchGroup>

            {/* Action Buttons */}
            <Flex
              gap="xs"
              justify="start"
              direction="row-reverse"
              className={styles.formButtons}
            >
              <Button
                variant="primary"
                size="large"
                type="submit"
                loading={formState.saving}
              >
                Save Tag
              </Button>
              <Button
                variant="tertiary"
                size="large"
                type="button"
                onClick={(): void => onOpenChange(false)}
              >
                Cancel
              </Button>
            </Flex>
          </form>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}

function validateTagName(
  name: string,
  existingTags: readonly Pick<Tag, 'name'>[],
  editMode: boolean,
  { submitting }: { submitting: boolean } = { submitting: false },
): string | null {
  if (!name.trim() && submitting) {
    return 'Name is required.'
  }
  if (name.length > 100) {
    return 'Name must be less than 100 characters.'
  }
  if (
    !editMode &&
    !isUnique(
      name,
      existingTags.map((tag) => tag.name),
    )
  ) {
    return 'This tag already exists.'
  }
  return null
}

function isUnique(name: string, list: string[]): boolean {
  return list.every((item) => {
    return item.toLowerCase() !== name.toLowerCase()
  })
}
